<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import type { UserOrganizationDto } from '@/api-client/models';
import { useAuthStore } from '@/stores/authStore';
import { handleGlobalError } from '@/composables/globalErrorHandler';
import { orgInitials } from '@/utils/orgInitials';

// The switcher behind the account menu's "Switch organization" row: every organization the user
// administers, the current one pinned to the top and marked. Deliberately just a list — no search,
// no recent section, no keyboard hints (Max, 2026-10-21). Choosing another organization hands off to
// authStore.switchOrganization, which re-authenticates with Kinde and redirects, so the dialog only
// has to hold the pending row until the browser leaves.

const visible = defineModel<boolean>('visible', { default: false });

const authStore = useAuthStore();
const pendingOrgCode = ref<string | null>(null);

const isCurrent = (org: UserOrganizationDto) => !!org.orgCode && org.orgCode === authStore.currentOrgId;

const organizations = computed(() =>
    [...authStore.adminOrganizations].sort((a, b) => {
        if (isCurrent(a) !== isCurrent(b)) return isCurrent(a) ? -1 : 1;
        return (a.name ?? a.orgCode ?? '').localeCompare(b.name ?? b.orgCode ?? '');
    })
);

const choose = async (org: UserOrganizationDto) => {
    if (pendingOrgCode.value) return;
    if (isCurrent(org)) {
        visible.value = false;
        return;
    }

    pendingOrgCode.value = org.orgCode ?? '';
    try {
        await authStore.switchOrganization(org.orgCode ?? '');
    } catch (error) {
        handleGlobalError(error);
        pendingOrgCode.value = null;
    }
};

// A reopened dialog starts clean even when an earlier attempt failed.
watch(visible, (open) => {
    if (open) pendingOrgCode.value = null;
});
</script>

<template>
    <Dialog
        v-model:visible="visible"
        modal
        header="Switch organization"
        :style="{ width: 'min(440px, 96vw)' }"
        :dismissable-mask="!pendingOrgCode"
        :closable="!pendingOrgCode"
        :pt="{ content: { style: 'padding: 6px 12px 12px' } }"
    >
        <ul class="org-switcher__list" role="list">
            <li v-for="org in organizations" :key="org.orgCode ?? org.organizationId ?? org.name ?? ''">
                <button
                    type="button"
                    class="org-switcher__row"
                    :class="{ 'org-switcher__row--current': isCurrent(org) }"
                    :disabled="!!pendingOrgCode"
                    :aria-current="isCurrent(org) ? 'true' : undefined"
                    @click="choose(org)"
                >
                    <span class="org-switcher__avatar" aria-hidden="true">{{ orgInitials(org.name ?? org.orgCode) }}</span>
                    <span class="org-switcher__name">{{ org.name ?? org.orgCode }}</span>
                    <span class="org-switcher__role">Admin</span>
                    <span v-if="pendingOrgCode === org.orgCode" class="org-switcher__state">
                        <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                        Switching
                    </span>
                    <span v-else-if="isCurrent(org)" class="org-switcher__state org-switcher__state--current">
                        <i class="pi pi-check" aria-hidden="true"></i>
                        Current
                    </span>
                </button>
            </li>
        </ul>
        <template #footer>
            <p class="org-switcher__note">Switching signs you in to the selected organization.</p>
        </template>
    </Dialog>
</template>

<style scoped>
.org-switcher__list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 340px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.org-switcher__row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 7px 8px;
    border: 0;
    border-radius: var(--tally-radius-xs);
    background: transparent;
    font: inherit;
    font-size: 13px;
    color: var(--tally-text-ink);
    text-align: left;
    cursor: pointer;
}

.org-switcher__row:hover:not(:disabled) {
    background: var(--tally-surface-tile);
}

.org-switcher__row:focus-visible {
    outline: 2px solid var(--tally-accent-blue);
    outline-offset: -2px;
}

.org-switcher__row:disabled {
    cursor: default;
}

.org-switcher__row--current {
    background: var(--tally-surface-tile);
}

.org-switcher__avatar {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: var(--tally-accent-blue-tint);
    color: var(--tally-brand-navy);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 600;
    flex-shrink: 0;
}

.org-switcher__row--current .org-switcher__avatar {
    background: var(--tally-brand-navy);
    color: var(--tally-text-inverse);
}

.org-switcher__name {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.org-switcher__row--current .org-switcher__name {
    font-weight: 500;
}

.org-switcher__role {
    padding: 1px 6px;
    border-radius: 999px;
    background: var(--tally-border-row);
    font-size: 10.5px;
    color: var(--tally-text-body);
    flex-shrink: 0;
}

.org-switcher__state {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10.5px;
    font-weight: 500;
    color: var(--tally-text-muted);
    flex-shrink: 0;
}

.org-switcher__state i {
    font-size: 11px;
}

.org-switcher__state--current {
    color: var(--tally-accent-blue);
}

.org-switcher__note {
    margin: 0;
    width: 100%;
    font-size: 11.5px;
    line-height: 1.4;
    color: var(--tally-text-muted);
}
</style>
